/** @format */

import axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { Store } from "redux";
import { clearUser } from "../redux/slices/userSlice";
import { pathRoute } from "../routes/PathRoute";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Interface for minimal store requirements
interface StoreLike {
  getState: () => any;
  dispatch: (action: any) => any;
}
const createAxiosInstance = (store: StoreLike): AxiosInstance => {
  const axiosInstance = axios.create({
    baseURL: BASE_URL,
  });

  // Interceptor for requests
  axiosInstance.interceptors.request.use((request) => {
    const storeData = store.getState();
    const authToken = storeData.user.token;
    if (authToken && !request.url?.includes("/countries")) {
      request.headers.Authorization = `Bearer ${authToken}`;
    }
    return request;
  });

  // Interceptor for responses
  axiosInstance.interceptors.response.use(
    (res) => {
      // You can add any logic here if needed, without Toast
      return res;
    },
    (e) => {
      const storeData = store.getState();

      if (storeData.user.token !== null) {
        if (e?.response?.data?.code === 401) {
          if (e.response.data.message) {
            console.error(`Unauthorized: ${e.response.data.message}`);
          }
          store.dispatch(clearUser());
          window.location.href = pathRoute.auth.login;
        } else if (
          e?.response?.data?.code === 404 ||
          e?.response?.data?.code === 500 ||
          e?.response?.data?.code === 422 ||
          e?.response?.data?.code === 409 ||
          e?.response?.data?.code === 413 ||
          e?.response?.data?.code === 403
        ) {
          // Log errors for specific codes
          console.error(`Error ${e?.response?.data?.code}: ${e?.response?.data?.message}`);
        } else {
          // Log all other error codes
          console.error(`Unexpected Error: ${e?.response?.data?.code} - ${e?.response?.data?.message}`);
        }
      } else {
        // Handle errors when the user is not authenticated
        console.error(`No token: ${e?.response?.data?.message}`);
      }

      return Promise.reject(e?.response); // Reject the promise so the calling function knows it failed
    }
  );

  return axiosInstance;
};

export const axiosGet2 = (axiosInstance: AxiosInstance, url: string, data?: object) => {
  return axiosInstance.get(url, {
    params: data,
  });
};

export const axiosPost2 = (
  axiosInstance: AxiosInstance,
  url: string,
  data: object,
  headers?: AxiosRequestConfig
) => {
  return axiosInstance.post(url, data, headers ? headers : {});
};

export const axiosConfig = (
  axiosInstance: AxiosInstance,
  method: string,
  url: string,
  config: AxiosRequestConfig,
  data: object
) => {
  return axiosInstance({
    method: method,
    url: url,
    ...config,
    data,
  });
};

export const axiosPatch = (axiosInstance: AxiosInstance, url: string, data: object) => {
  return axiosInstance.patch(url, data);
};

export const axiosPut = (axiosInstance: AxiosInstance, url: string, data: object) => {
  return axiosInstance.put(url, data);
};

export const axiosDelete = (axiosInstance: AxiosInstance, url: string, data?: object) => {
  return axiosInstance.delete(url, { data });
};

export default createAxiosInstance;